import type { DataSourceConnectionKind } from '@erp/shared-types';
import { connectionKindOf } from '@erp/shared-types';
import { useAllDataSourceLinks } from 'src/api/rpc/hooks/core/useDataSources';
import { useListSchemaDefinitions } from 'src/api/rpc/hooks/core/useSchemaDefinitions';

/**
 * Shared DataSource Link selection used by the Observe DataSource Link trigger
 * and the CSV/FWF DataSource Adapter steps. Mirrors the backend
 * `dataSourceLinkSelectionSchema`:
 *   - 'static'          — one link by `dataSourceLinkId`.
 *   - 'matching_schema' — every link conforming to `matchingSchemaId`.
 */
export type DataSourceLinkStrategy = 'static' | 'matching_schema';

export function useDataSourceLinkSelection({
  config,
  onConfigChange,
  resetKeys = [],
  resetOnStrategyChange = false,
  connectionKind,
}: {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void | Promise<unknown>;
  /** Config keys to clear whenever the selected link/schema changes. */
  resetKeys?: ReadonlyArray<string>;
  /** Also clear `resetKeys` when the strategy itself changes. */
  resetOnStrategyChange?: boolean;
  /**
   * Only offer links whose DataSource connection belongs to this kind (e.g.
   * 'blob' for the Blob Storage adapter). Unset = all links.
   */
  connectionKind?: DataSourceConnectionKind;
}) {
  const {
    data: allLinksData,
    isLoading: linksLoading,
    isError: linksError,
  } = useAllDataSourceLinks();
  const { data: schemasData } = useListSchemaDefinitions();

  const groups = allLinksData?.groups ?? [];
  const groupedLinks = connectionKind
    ? groups.flatMap((group) => {
        const links = group.links.filter(
          (link) => connectionKindOf(link.connectionType) === connectionKind,
        );
        return links.length > 0 ? [{ ...group, links }] : [];
      })
    : groups;
  const allLinks = groupedLinks.flatMap((g) => g.links);

  const strategy =
    (config.dataSourceLinkStrategy as DataSourceLinkStrategy | undefined) ??
    undefined;
  const selectedLinkId =
    typeof config.dataSourceLinkId === 'string' ? config.dataSourceLinkId : '';
  const selectedLink = allLinks.find((l) => l.id === selectedLinkId);
  const matchingSchemaId =
    typeof config.matchingSchemaId === 'string' ? config.matchingSchemaId : '';
  const matchingTableTemplate =
    typeof config.matchingTableTemplate === 'string'
      ? config.matchingTableTemplate
      : '';

  const matchedLinks = matchingSchemaId
    ? allLinks.filter((l) => l.schemaId === matchingSchemaId)
    : [];

  // The schema whose fields drive column-mapping options: the static link's
  // schema, or the matching-schema selection.
  const schemaIdForMapping =
    strategy === 'static'
      ? (selectedLink?.schemaId ?? '')
      : strategy === 'matching_schema'
        ? matchingSchemaId
        : '';

  const reset = Object.fromEntries(resetKeys.map((k) => [k, undefined]));

  function handleStrategyChange(v: string) {
    void onConfigChange({
      ...config,
      dataSourceLinkStrategy:
        v === '' ? undefined : (v as DataSourceLinkStrategy),
      dataSourceLinkId: undefined,
      matchingSchemaId: undefined,
      matchingTableTemplate: undefined,
      ...(resetOnStrategyChange ? reset : {}),
    });
  }

  function handleLinkChange(value: string) {
    void onConfigChange({
      ...config,
      dataSourceLinkId: value || undefined,
      ...reset,
    });
  }

  function handleMatchingSchemaChange(value: string) {
    void onConfigChange({
      ...config,
      matchingSchemaId: value || undefined,
      ...reset,
    });
  }

  function handleMatchingTableTemplateChange(value: string) {
    void onConfigChange({
      ...config,
      matchingTableTemplate: value || undefined,
    });
  }

  return {
    groupedLinks,
    allLinks,
    linksLoading,
    linksError,
    schemasData,
    strategy,
    selectedLinkId,
    selectedLink,
    matchingSchemaId,
    matchingTableTemplate,
    matchedLinks,
    schemaIdForMapping,
    handleStrategyChange,
    handleLinkChange,
    handleMatchingSchemaChange,
    handleMatchingTableTemplateChange,
  };
}

export type DataSourceLinkSelection = ReturnType<
  typeof useDataSourceLinkSelection
>;
